/**
 * This tab's identity for content sync (PLAN.md "Client engine": "Per device:
 * `client_id` (one per tab on the web) and `next_seq`. A seq is never reused.").
 *
 * - `clientId` lives in `sessionStorage`: one per tab, kept across reloads.
 *   A duplicated tab copies it: each tab claims its id on a BroadcastChannel,
 *   a tab holding it answers, and the newcomer mints its own. The claim goes
 *   out when the device is created (at startup), and the first seq waits up
 *   to `CLAIM_WAIT_MS` for a `taken`, so a duplicate never sends under the
 *   copied id.
 * - Seqs therefore come from ONE counter shared by every tab of the browser
 *   (`localStorage`), taken under a Web Lock where the browser has them, so
 *   no two changes ever carry the same `(clientId, seq)`. Gaps are harmless:
 *   the server only compares a seq with the last one it saw.
 * - The client id is tied to the counter's lifetime: when the counter is gone
 *   (sign-out clears localStorage) or broken, the next read mints a new one.
 *
 * Each tab keeps its own copy of the SQLite database, so neither value can
 * live in the `meta` table.
 */

import { generateId } from "@caliu/utils";
import type { SyncDeviceSource } from "./services/contentSync";

const CLIENT_ID_KEY = "caliu-sync-client-id";
/** The counter generation this tab's client id was minted under. */
const CLIENT_EPOCH_KEY = "caliu-sync-client-epoch";
const NEXT_SEQ_KEY = "caliu-sync-next-seq";
/** Lives and dies with the counter: a new one means every seq starts over. */
const SEQ_EPOCH_KEY = "caliu-sync-seq-epoch";
const LOCK_NAME = "caliu-sync-seq";

function validSeq(raw: string | null): number | null {
  const n = Number(raw);
  return raw !== null && Number.isSafeInteger(n) && n >= 1 ? n : null;
}

/**
 * The seq counter's generation. A missing or broken counter (sign-out wipes
 * localStorage, but not this tab's sessionStorage) starts a new generation at
 * seq 1, so every tab mints a new client id: a client id never sees a seq twice.
 */
function seqEpoch(): string {
  let epoch = localStorage.getItem(SEQ_EPOCH_KEY);
  if (!epoch || validSeq(localStorage.getItem(NEXT_SEQ_KEY)) === null) {
    epoch = generateId(12);
    localStorage.setItem(SEQ_EPOCH_KEY, epoch);
    localStorage.setItem(NEXT_SEQ_KEY, "1");
  }
  return epoch;
}

const CHANNEL_NAME = "caliu-sync-client-id";
type ClaimMessage = { type: "claim" | "taken"; id: string };
let channel: BroadcastChannel | null = null;
/** The id this page has claimed (null: none yet). */
let claimed: string | null = null;

/** How long the first send waits for another tab to say the id is taken. */
export const CLAIM_WAIT_MS = 200;
/** Settles when the first claim's window closes, or at once on a `taken`. */
let claimWindow: Promise<void> | null = null;
let endClaimWindow: (() => void) | null = null;

/**
 * Tells the other tabs this page uses `id`. A tab already using it answers
 * `taken` (this tab is a duplicate, its sessionStorage copied): this one
 * forgets the id and mints its own on the next read.
 */
function claim(id: string): void {
  if (claimed === id) return;
  claimed = id;
  if (typeof BroadcastChannel === "undefined") return;
  if (!channel) {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (ev: MessageEvent<ClaimMessage>) => {
      const m = ev.data;
      if (!m || m.id !== claimed) return;
      if (m.type === "claim") channel?.postMessage({ type: "taken", id: m.id } satisfies ClaimMessage);
      else if (m.type === "taken") {
        try {
          if (sessionStorage.getItem(CLIENT_ID_KEY) === m.id) sessionStorage.removeItem(CLIENT_ID_KEY);
        } catch {
          // Storage blocked: ids are minted per read then, never shared.
        }
        claimed = null;
        endClaimWindow?.();
      }
    };
  }
  claimWindow ??= new Promise<void>((resolve) => {
    endClaimWindow = resolve;
    setTimeout(resolve, CLAIM_WAIT_MS);
  });
  channel.postMessage({ type: "claim", id } satisfies ClaimMessage);
}

function tabClientId(): string {
  try {
    const epoch = seqEpoch();
    let id = sessionStorage.getItem(CLIENT_ID_KEY);
    if (!id || sessionStorage.getItem(CLIENT_EPOCH_KEY) !== epoch) {
      id = `web-${generateId(16)}`;
      sessionStorage.setItem(CLIENT_ID_KEY, id);
      sessionStorage.setItem(CLIENT_EPOCH_KEY, epoch);
    }
    claim(id);
    return id;
  } catch {
    // Storage blocked (private mode): a fresh id per read is still unique.
    return `web-${generateId(16)}`;
  }
}

/** Takes the next seq from the shared counter (read and write in one step). */
function takeSeq(): number {
  seqEpoch();
  const seq = validSeq(localStorage.getItem(NEXT_SEQ_KEY)) ?? 1;
  localStorage.setItem(NEXT_SEQ_KEY, String(seq + 1));
  return seq;
}

/** Forgets this tab's client id (sign-out). */
export function clearTabClientId(): void {
  try {
    sessionStorage.removeItem(CLIENT_ID_KEY);
    sessionStorage.removeItem(CLIENT_EPOCH_KEY);
  } catch {
    // Storage blocked: there is nothing stored to forget.
  }
}

export class WebSyncDevice implements SyncDeviceSource {
  /** Claims this tab's client id now (startup), so a duplicate learns it early. */
  constructor() {
    tabClientId();
  }

  /** Settles once no other tab said this tab's id is taken (at most `CLAIM_WAIT_MS` after the claim). */
  ready(): Promise<void> {
    tabClientId();
    return claimWindow ?? Promise.resolve();
  }

  /**
   * Read each pass (after `allocateSeq`, which may start a new counter
   * generation): never cached, so a wiped counter brings a new client id.
   */
  get clientId(): string {
    return tabClientId();
  }

  async allocateSeq(): Promise<number> {
    await this.ready();
    const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
    if (locks) return locks.request(LOCK_NAME, () => takeSeq());
    return takeSeq();
  }
}
